import { API_BASE_URL } from '../../config/api'
import { authFetch } from '../../utils/authFetch'
import React, { useState, useEffect } from 'react'
import { 
  CreditCard,
  DollarSign,
  TrendingUp,
  Calendar,
  Package,
  Users,
  AlertCircle,
  CheckCircle2,
  Clock,
  Download,
  Eye,
  RefreshCw,
  Plus,
  Search,
  Filter,
  Edit3,
  Trash2,
  X
} from 'lucide-react'

interface SubscriptionPlan {
  id: string
  name: string
  price: number
  interval: 'monthly' | 'yearly'
  features: string[]
  maxBookings: number
  maxApiCalls: number
  customBranding: boolean
  priority: 'low' | 'medium' | 'high'
}

interface Subscription {
  id: string
  operatorName: string
  operatorEmail: string
  planId: string
  planName: string
  status: 'active' | 'canceled' | 'past_due' | 'trialing'
  currentPeriodStart: string
  currentPeriodEnd: string
  amount: number
  interval: 'monthly' | 'yearly'
  nextPayment: string
  usage: {
    bookings: number
    apiCalls: number
  }
}

const ADMIN_BRAND = {
  primary: '#4F46E5',
  primaryLight: '#EEF2FF',
  primaryText: '#3730A3',
  gradient: 'linear-gradient(to right, #2563EB, #4F46E5)',
}

const SubscriptionManagement: React.FC = () => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([])
  const [plans, setPlans] = useState<SubscriptionPlan[]>([])
  const [loading, setLoading] = useState(true)

  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [planFilter, setPlanFilter] = useState<string>('all')
  
  // Plan management state
  const [showCreatePlanModal, setShowCreatePlanModal] = useState(false)
  const [showEditPlanModal, setShowEditPlanModal] = useState(false)
  const [showDeletePlanModal, setShowDeletePlanModal] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null)
  const [creatingPlan, setCreatingPlan] = useState(false)
  const [editingPlan, setEditingPlan] = useState(false)
  const [deletingPlan, setDeletingPlan] = useState(false)
  
  const [newPlanData, setNewPlanData] = useState({
    name: '',
    display_name: '',
    description: '',
    price_monthly: 0,
    price_yearly: 0,
    features: [] as string[],
    max_bookings: 100,
    max_api_calls: 5000,
    custom_branding: false,
    white_label: false,
    priority_support: false
  })
  
  const [editPlanData, setEditPlanData] = useState({
    display_name: '',
    description: '',
    price_monthly: 0,
    price_yearly: 0,
    features: [] as string[],
    max_bookings: 100,
    max_api_calls: 5000,
    custom_branding: false,
    white_label: false,
    priority_support: false,
    is_active: true
  })
  
  const [featureInput, setFeatureInput] = useState('')

  // Fetch subscription data from API
  useEffect(() => {
    const fetchSubscriptions = async () => {
      try {
        setLoading(true)
        
        const response = await authFetch(`${API_BASE_URL}/api/super-admin/subscriptions`)

        if (response.ok) {
          const data = await response.json()

          if (data.success) {
            setSubscriptions(data.subscriptions)
            setPlans(data.plans)
          } else {
            console.error('Subscriptions API returned error:', data)
          }
        } else {
          console.error('Subscriptions request failed with status:', response.status)
        }
      } catch (error) {
        console.error('Error fetching subscriptions:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchSubscriptions()
  }, [])

  // Plan management functions
  const handleCreatePlan = async () => {
    if (!newPlanData.name || !newPlanData.display_name || newPlanData.price_monthly <= 0) {
      alert('Please fill in all required fields')
      return
    }

    try {
      setCreatingPlan(true)
      
      const response = await authFetch(`${API_BASE_URL}/api/super-admin/plans`, {
        method: 'POST',
        body: JSON.stringify(newPlanData)
      })
      
      if (response.ok) {
        const data = await response.json()
        
        if (data.success) {
          // Add the new plan to the list
          const newPlan = {
            id: data.plan.id,
            name: data.plan.display_name,
            price: data.plan.price_monthly,
            interval: 'monthly' as 'monthly' | 'yearly',
            features: data.plan.features,
            maxBookings: data.plan.max_bookings,
            maxApiCalls: data.plan.max_api_calls,
            customBranding: data.plan.custom_branding,
            priority: data.plan.priority_support ? 'high' as 'low' | 'medium' | 'high' : 'medium' as 'low' | 'medium' | 'high'
          }
          
          setPlans(prevPlans => [...prevPlans, newPlan])
          
          // Reset form and close modal
          setNewPlanData({
            name: '',
            display_name: '',
            description: '',
            price_monthly: 0,
            price_yearly: 0,
            features: [],
            max_bookings: 100,
            max_api_calls: 5000,
            custom_branding: false,
            white_label: false,
            priority_support: false
          })
          setShowCreatePlanModal(false)
          alert('Plan created successfully!')
        } else {
          alert(`Error: ${data.error}`)
        }
      } else {
        alert('Failed to create plan')
      }
    } catch (error) {
      console.error('Error creating plan:', error)
      alert('Error creating plan')
    } finally {
      setCreatingPlan(false)
    }
  }

  const handleEditPlan = (plan: SubscriptionPlan) => {
    setSelectedPlan(plan)
    setEditPlanData({
      display_name: plan.name,
      description: `${plan.name} plan with ${plan.maxBookings === -1 ? 'unlimited' : plan.maxBookings} bookings`,
      price_monthly: plan.price,
      price_yearly: plan.price * 12,
      features: plan.features,
      max_bookings: plan.maxBookings,
      max_api_calls: plan.maxApiCalls,
      custom_branding: plan.customBranding,
      white_label: plan.name.toLowerCase() === 'enterprise',
      priority_support: plan.priority === 'high',
      is_active: true
    })
    setShowEditPlanModal(true)
  }

  const handleUpdatePlan = async () => {
    if (!selectedPlan || !editPlanData.display_name || editPlanData.price_monthly <= 0) {
      alert('Please fill in all required fields')
      return
    }

    try {
      setEditingPlan(true)
      
      const planName = selectedPlan.name.toLowerCase().replace(' ', '_')
      const response = await authFetch(`${API_BASE_URL}/api/super-admin/plans/${planName}`, {
        method: 'PUT',
        body: JSON.stringify(editPlanData)
      })
      
      if (response.ok) {
        const data = await response.json()
        
        if (data.success) {
          // Update the plan in the list
          setPlans(prevPlans => 
            prevPlans.map(plan => 
              plan.id === selectedPlan.id 
                ? {
                    ...plan,
                    name: editPlanData.display_name,
                    price: editPlanData.price_monthly,
                    features: editPlanData.features,
                    maxBookings: editPlanData.max_bookings,
                    maxApiCalls: editPlanData.max_api_calls,
                    customBranding: editPlanData.custom_branding,
                    priority: editPlanData.priority_support ? 'high' as 'low' | 'medium' | 'high' : 'medium' as 'low' | 'medium' | 'high'
                  }
                : plan
            )
          )
          
          setShowEditPlanModal(false)
          setSelectedPlan(null)
          alert('Plan updated successfully!')
        } else {
          alert(`Error: ${data.error}`)
        }
      } else {
        alert('Failed to update plan')
      }
    } catch (error) {
      console.error('Error updating plan:', error)
      alert('Error updating plan')
    } finally {
      setEditingPlan(false)
    }
  }

  const handleDeletePlan = (plan: SubscriptionPlan) => {
    setSelectedPlan(plan)
    setShowDeletePlanModal(true)
  }

  const handleConfirmDeletePlan = async () => {
    if (!selectedPlan) return

    try {
      setDeletingPlan(true)
      
      const planName = selectedPlan.name.toLowerCase().replace(' ', '_')
      const response = await authFetch(`${API_BASE_URL}/api/super-admin/plans/${planName}`, {
        method: 'DELETE'
      })
      
      if (response.ok) {
        const data = await response.json()
        
        if (data.success) {
          // Remove the plan from the list
          setPlans(prevPlans => 
            prevPlans.filter(plan => plan.id !== selectedPlan.id)
          )
          
          setShowDeletePlanModal(false)
          setSelectedPlan(null)
          alert('Plan deleted successfully!')
        } else {
          alert(`Error: ${data.error}`)
        }
      } else {
        const errorData = await response.json()
        alert(errorData.error || 'Failed to delete plan')
      }
    } catch (error) {
      console.error('Error deleting plan:', error)
      alert('Error deleting plan')
    } finally {
      setDeletingPlan(false)
    }
  }

  const addFeature = () => {
    if (featureInput.trim() && newPlanData.features.length < 10) {
      setNewPlanData({
        ...newPlanData,
        features: [...newPlanData.features, featureInput.trim()]
      })
      setFeatureInput('')
    }
  }

  const removeFeature = (index: number) => {
    setNewPlanData({
      ...newPlanData,
      features: newPlanData.features.filter((_, i) => i !== index)
    })
  }

  const addEditFeature = () => {
    if (featureInput.trim() && editPlanData.features.length < 10) {
      setEditPlanData({
        ...editPlanData,
        features: [...editPlanData.features, featureInput.trim()]
      })
      setFeatureInput('')
    }
  }

  const removeEditFeature = (index: number) => {
    setEditPlanData({
      ...editPlanData,
      features: editPlanData.features.filter((_, i) => i !== index)
    })
  }

  const filteredSubscriptions = subscriptions.filter(subscription => {
    const matchesSearch = subscription.operatorName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         subscription.operatorEmail.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === 'all' || subscription.status === statusFilter
    const matchesPlan = planFilter === 'all' || subscription.planName.toLowerCase() === planFilter.toLowerCase()
    
    return matchesSearch && matchesStatus && matchesPlan
  })

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800'
      case 'trialing': return 'bg-blue-100 text-blue-800'
      case 'past_due': return 'bg-red-100 text-red-800'
      case 'canceled': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getPlanColor = (planName: string): { className: string; style?: React.CSSProperties } => {
    switch (planName.toLowerCase()) {
      case 'starter': return { className: 'bg-blue-100 text-blue-800' }
      case 'professional': return { className: '', style: { backgroundColor: ADMIN_BRAND.primaryLight, color: ADMIN_BRAND.primaryText } }
      case 'enterprise': return { className: 'bg-orange-100 text-orange-800' }
      default: return { className: 'bg-gray-100 text-gray-800' }
    }
  }

  const totalMRR = subscriptions
    .filter(sub => sub.status === 'active')
    .reduce((sum, sub) => sum + (sub.interval === 'yearly' ? sub.amount / 12 : sub.amount), 0)

  const totalARR = subscriptions
    .filter(sub => sub.status === 'active')
    .reduce((sum, sub) => sum + (sub.interval === 'yearly' ? sub.amount : sub.amount * 12), 0)

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="rounded-xl p-6 text-white" style={{ background: ADMIN_BRAND.gradient }}>
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mr-3"></div>
            <span className="text-lg">Loading subscription data...</span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <CreditCard className="h-6 w-6 text-blue-600 mr-2" />
            Subscription & Billing Management
          </h1>
          <p className="text-gray-600 mt-1">Manage subscription plans, billing, and revenue analytics</p>
        </div>
        <button 
          onClick={() => setShowCreatePlanModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Plan
        </button>
      </div>

      {/* Revenue Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="flex items-center">
            <DollarSign className="h-8 w-8 text-green-600 bg-green-100 rounded-lg p-2" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Monthly Recurring Revenue</p>
              <p className="text-2xl font-bold text-gray-900">${Math.round(totalMRR).toLocaleString()}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="flex items-center">
            <TrendingUp className="h-8 w-8 text-blue-600 bg-blue-100 rounded-lg p-2" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Annual Recurring Revenue</p>
              <p className="text-2xl font-bold text-gray-900">${Math.round(totalARR).toLocaleString()}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="flex items-center">
            <CheckCircle2 className="h-8 w-8 text-green-600 bg-green-100 rounded-lg p-2" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Active Subscriptions</p>
              <p className="text-2xl font-bold text-gray-900">
                {subscriptions.filter(sub => sub.status === 'active').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-gray-200">
          <div className="flex items-center">
            <AlertCircle className="h-8 w-8 text-red-600 bg-red-100 rounded-lg p-2" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500">Past Due</p>
              <p className="text-2xl font-bold text-gray-900">
                {subscriptions.filter(sub => sub.status === 'past_due').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Plans */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Package className="h-5 w-5 text-gray-400 mr-2" />
            Subscription Plans
          </h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan) => (
              <div key={plan.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">{plan.name}</h4>
                    <div className="flex items-baseline">
                      <span className="text-2xl font-bold text-gray-900">${plan.price}</span>
                      <span className="text-sm text-gray-500 ml-1">/{plan.interval}</span>
                    </div>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPlanColor(plan.name).className}`} style={getPlanColor(plan.name).style}>
                    {plan.name}
                  </span>
                </div>
                <ul className="space-y-2">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-center text-sm text-gray-600">
                      <CheckCircle2 className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="text-xs text-gray-500 space-y-1">
                    <div>Max Bookings: {plan.maxBookings === -1 ? 'Unlimited' : plan.maxBookings.toLocaleString()}</div>
                    <div>API Calls: {plan.maxApiCalls === -1 ? 'Unlimited' : plan.maxApiCalls.toLocaleString()}</div>
                    <div>Custom Branding: {plan.customBranding ? 'Yes' : 'No'}</div>
                  </div>
                  
                  <div className="flex items-center space-x-2 mt-4">
                    <button 
                      onClick={() => handleEditPlan(plan)}
                      className="flex-1 px-3 py-2 text-xs font-medium text-blue-600 border border-blue-200 rounded-md hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <Edit3 className="h-3 w-3 inline mr-1" />
                      Edit
                    </button>
                    <button 
                      onClick={() => handleDeletePlan(plan)}
                      className="flex-1 px-3 py-2 text-xs font-medium text-red-600 border border-red-200 rounded-md hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500"
                    >
                      <Trash2 className="h-3 w-3 inline mr-1" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search subscriptions..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <select
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="trialing">Trial</option>
            <option value="past_due">Past Due</option>
            <option value="canceled">Canceled</option>
          </select>
          <select
            className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value)}
          >
            <option value="all">All Plans</option>
            <option value="starter">Starter</option>
            <option value="professional">Professional</option>
            <option value="enterprise">Enterprise</option>
          </select>
        </div>
      </div>

      {/* Subscriptions Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plan & Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Usage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Billing
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredSubscriptions.map((subscription) => (
                <tr key={subscription.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <Users className="h-5 w-5 text-blue-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{subscription.operatorName}</div>
                        <div className="text-sm text-gray-500">{subscription.operatorEmail}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPlanColor(subscription.planName).className}`} style={getPlanColor(subscription.planName).style}>
                        {subscription.planName}
                      </span>
                      <div>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusColor(subscription.status)}`}>
                          {subscription.status}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="space-y-1">
                      <div>{subscription.usage.bookings} bookings</div>
                      <div className="text-xs text-gray-500">{subscription.usage.apiCalls.toLocaleString()} API calls</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="space-y-1">
                      <div className="font-medium">${subscription.amount}/{subscription.interval}</div>
                      <div className="text-xs text-gray-500 flex items-center">
                        <Calendar className="h-3 w-3 mr-1" />
                        Next: {new Date(subscription.nextPayment).toLocaleDateString()}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center space-x-2">
                      <button className="text-blue-600 hover:text-blue-900">
                        <Eye className="h-4 w-4" />
                      </button>
                      <button className="text-gray-600 hover:text-gray-900">
                        <RefreshCw className="h-4 w-4" />
                      </button>
                      <button className="text-gray-600 hover:text-gray-900">
                        <Download className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Plan Modal */}
      {showCreatePlanModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-6 border w-full max-w-2xl shadow-lg rounded-lg bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Create New Subscription Plan</h3>
              <button
                onClick={() => setShowCreatePlanModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); handleCreatePlan(); }} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Plan Name *</label>
                  <input
                    type="text"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Premium"
                    value={newPlanData.name}
                    onChange={(e) => setNewPlanData({...newPlanData, name: e.target.value})}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Display Name *</label>
                  <input
                    type="text"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., Premium Plan"
                    value={newPlanData.display_name}
                    onChange={(e) => setNewPlanData({...newPlanData, display_name: e.target.value})}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Plan description"
                  rows={3}
                  value={newPlanData.description}
                  onChange={(e) => setNewPlanData({...newPlanData, description: e.target.value})}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Price ($) *</label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={newPlanData.price_monthly}
                    onChange={(e) => setNewPlanData({...newPlanData, price_monthly: parseFloat(e.target.value) || 0})}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Yearly Price ($)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={newPlanData.price_yearly}
                    onChange={(e) => setNewPlanData({...newPlanData, price_yearly: parseFloat(e.target.value) || 0})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Bookings (-1 for unlimited)</label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={newPlanData.max_bookings}
                    onChange={(e) => setNewPlanData({...newPlanData, max_bookings: parseInt(e.target.value) || 0})}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max API Calls (-1 for unlimited)</label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={newPlanData.max_api_calls}
                    onChange={(e) => setNewPlanData({...newPlanData, max_api_calls: parseInt(e.target.value) || 0})}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Features</label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Add feature"
                    value={featureInput}
                    onChange={(e) => setFeatureInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                  />
                  <button
                    type="button"
                    onClick={addFeature}
                    className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                  >
                    Add
                  </button>
                </div>
                <div className="mt-2 space-y-1">
                  {newPlanData.features.map((feature, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded">
                      <span className="text-sm">{feature}</span>
                      <button
                        type="button"
                        onClick={() => removeFeature(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center space-x-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    checked={newPlanData.custom_branding}
                    onChange={(e) => setNewPlanData({...newPlanData, custom_branding: e.target.checked})}
                  />
                  <span className="ml-2 text-sm text-gray-700">Custom Branding</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    checked={newPlanData.white_label}
                    onChange={(e) => setNewPlanData({...newPlanData, white_label: e.target.checked})}
                  />
                  <span className="ml-2 text-sm text-gray-700">White Label</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    checked={newPlanData.priority_support}
                    onChange={(e) => setNewPlanData({...newPlanData, priority_support: e.target.checked})}
                  />
                  <span className="ml-2 text-sm text-gray-700">Priority Support</span>
                </label>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreatePlanModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={creatingPlan}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creatingPlan}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {creatingPlan ? 'Creating...' : 'Create Plan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Plan Modal */}
      {showEditPlanModal && selectedPlan && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-6 border w-full max-w-2xl shadow-lg rounded-lg bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Edit {selectedPlan.name} Plan</h3>
              <button
                onClick={() => setShowEditPlanModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); handleUpdatePlan(); }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Display Name *</label>
                <input
                  type="text"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  value={editPlanData.display_name}
                  onChange={(e) => setEditPlanData({...editPlanData, display_name: e.target.value})}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  rows={3}
                  value={editPlanData.description}
                  onChange={(e) => setEditPlanData({...editPlanData, description: e.target.value})}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Price ($) *</label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={editPlanData.price_monthly}
                    onChange={(e) => setEditPlanData({...editPlanData, price_monthly: parseFloat(e.target.value) || 0})}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Yearly Price ($)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={editPlanData.price_yearly}
                    onChange={(e) => setEditPlanData({...editPlanData, price_yearly: parseFloat(e.target.value) || 0})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Bookings</label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={editPlanData.max_bookings}
                    onChange={(e) => setEditPlanData({...editPlanData, max_bookings: parseInt(e.target.value) || 0})}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max API Calls</label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={editPlanData.max_api_calls}
                    onChange={(e) => setEditPlanData({...editPlanData, max_api_calls: parseInt(e.target.value) || 0})}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Features</label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Add feature"
                    value={featureInput}
                    onChange={(e) => setFeatureInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addEditFeature())}
                  />
                  <button
                    type="button"
                    onClick={addEditFeature}
                    className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                  >
                    Add
                  </button>
                </div>
                <div className="mt-2 space-y-1">
                  {editPlanData.features.map((feature, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded">
                      <span className="text-sm">{feature}</span>
                      <button
                        type="button"
                        onClick={() => removeEditFeature(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center space-x-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    checked={editPlanData.custom_branding}
                    onChange={(e) => setEditPlanData({...editPlanData, custom_branding: e.target.checked})}
                  />
                  <span className="ml-2 text-sm text-gray-700">Custom Branding</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    checked={editPlanData.white_label}
                    onChange={(e) => setEditPlanData({...editPlanData, white_label: e.target.checked})}
                  />
                  <span className="ml-2 text-sm text-gray-700">White Label</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    checked={editPlanData.priority_support}
                    onChange={(e) => setEditPlanData({...editPlanData, priority_support: e.target.checked})}
                  />
                  <span className="ml-2 text-sm text-gray-700">Priority Support</span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    checked={editPlanData.is_active}
                    onChange={(e) => setEditPlanData({...editPlanData, is_active: e.target.checked})}
                  />
                  <span className="ml-2 text-sm text-gray-700">Active</span>
                </label>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditPlanModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={editingPlan}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={editingPlan}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {editingPlan ? 'Updating...' : 'Update Plan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Plan Modal */}
      {showDeletePlanModal && selectedPlan && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-6 border w-full max-w-md shadow-lg rounded-lg bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-red-600">Delete Subscription Plan</h3>
              <button
                onClick={() => setShowDeletePlanModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="mb-6">
              <div className="flex items-center mb-4">
                <AlertCircle className="h-12 w-12 text-red-600" />
                <div className="ml-4">
                  <h4 className="text-lg font-medium text-gray-900">Are you sure?</h4>
                  <p className="text-sm text-gray-500">This action cannot be undone.</p>
                </div>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm">
                  <p><strong>Plan:</strong> {selectedPlan.name}</p>
                  <p><strong>Price:</strong> ${selectedPlan.price}/{selectedPlan.interval}</p>
                  <p><strong>Max Bookings:</strong> {selectedPlan.maxBookings === -1 ? 'Unlimited' : selectedPlan.maxBookings}</p>
                  <p><strong>Features:</strong> {selectedPlan.features.length} features</p>
                </div>
              </div>
              
              <p className="text-sm text-red-600 mt-4">
                Deleting this plan will prevent new subscriptions but won't affect existing subscribers.
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => setShowDeletePlanModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={deletingPlan}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDeletePlan}
                disabled={deletingPlan}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
              >
                {deletingPlan ? 'Deleting...' : 'Delete Plan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SubscriptionManagement